import {useCallback} from 'react';

/**
 * Hook to handle snippet drop events for source package containers.
 *
 * @param {Object} params
 * @param {Function} params.addItem - Store action to add an item
 * @param {Function} [params.moveItem] - Store action to move an existing item
 * @param {string|null} params.folderId - Target folder ID (null for root)
 * @param {string} params.userId - Current user ID
 * @returns {Object} Drag event handlers
 */
export const useSnippetDrop = ({addItem, moveItem, folderId, userId}) => {
    const handleDragOver = useCallback((event) => {
        if (event.dataTransfer) {
            event.preventDefault();
        }
    }, []);

    const handleDrop = useCallback((event) => {
        try {
            const raw = event.dataTransfer?.getData('application/x-snippet');
            if (!raw) return;

            const data = JSON.parse(raw);

            // If the dragged item already lives in the source package, move it
            if (data.itemId && moveItem) {
                moveItem(data.itemId, folderId || null);
                return;
            }

            // Validate content before adding
            // Skip validation for whole documents (no start/end/index/segmentId means it's a document)
            const isSegment = data?.start || data?.end || data?.index != null || data?.segmentId;
            if (isSegment && (!data?.content || !data.content.trim())) {
                console.warn('Cannot drop segment with empty content');
                return;
            }

            addItem({type: 'snippet', parentFolderId: folderId || null, userId, ...data});
        } catch {
            // Silently ignore invalid drops
        } finally {
            event.preventDefault();
            event.stopPropagation();
        }
    }, [addItem, moveItem, folderId, userId]);

    return {
        onDragOver: handleDragOver,
        onDrop: handleDrop,
    };
};
